import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, admin, errMsg } from "@/lib/api";

const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const NUMS = [["stale_seconds", "Stale after (sec)"], ["on_demand_ttl_minutes", "On-demand TTL (min)"],
  ["client_rate_limit_per_min", "On-demand actions / min / client"], ["max_total_subscriptions", "Max total subscriptions"]];

const Area = ({ label, value, onChange, testid, rows = 6 }) => (
  <div className="space-y-1">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <Textarea data-testid={testid} rows={rows} value={value} onChange={(e) => onChange(e.target.value)}
      className="scroll-thin border-slate-800 bg-slate-900 font-mono text-xs" />
  </div>
);

export const ConfigTab = ({ pw }) => {
  const [f, setF] = useState(null);
  const [unresolved, setUnresolved] = useState({});
  const [busy, setBusy] = useState(false);

  const load = (cfg, unr) => {
    setF({
      curated_indices: cfg.curated_indices.join("\n"), watchlist: cfg.watchlist.join("\n"), universe: cfg.universe.join("\n"),
      holidays: cfg.holidays.map((h) => `${h.date} ${h.name || ""}`.trim()).join("\n"),
      special_sessions: cfg.special_sessions.map((s) => `${s.date} ${s.open}-${s.close} ${s.name || ""}`.trim()).join("\n"),
      caps: JSON.stringify(cfg.caps), instrument_sync_time_ist: cfg.instrument_sync_time_ist,
      risk_free_rate: String(cfg.risk_free_rate),
      ...Object.fromEntries(NUMS.map(([k]) => [k, String(cfg[k])])),
    });
    setUnresolved(unr || {});
  };

  useEffect(() => {
    api.get("/admin/config", admin(pw)).then((r) => load(r.data.config, r.data.unresolved)).catch((e) => toast.error(errMsg(e)));
  }, [pw]);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        curated_indices: lines(f.curated_indices), watchlist: lines(f.watchlist), universe: lines(f.universe),
        holidays: lines(f.holidays).map((l) => ({ date: l.slice(0, 10), name: l.slice(11).trim() })),
        special_sessions: lines(f.special_sessions).map((l) => {
          const [date, times, ...rest] = l.split(" ");
          const [open, close] = (times || "18:00-19:00").split("-");
          return { date, open, close, name: rest.join(" ") };
        }),
        caps: JSON.parse(f.caps), instrument_sync_time_ist: f.instrument_sync_time_ist,
        risk_free_rate: Number(f.risk_free_rate),
        ...Object.fromEntries(NUMS.map(([k]) => [k, Number(f[k])])),
      };
      const { data } = await api.put("/admin/config", body, admin(pw));
      load(data.config, data.unresolved);
      const miss = Object.values(data.unresolved).flat();
      miss.length ? toast.warning(`Saved. Not found in instrument master: ${miss.join(", ")}`) : toast.success("Configuration saved");
    } catch (e) {
      toast.error(e instanceof SyntaxError ? "Caps must be valid JSON" : errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!f) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-500" /></div>;
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const miss = Object.values(unresolved).flat();
  return (
    <div className="space-y-4 pt-3" data-testid="config-tab">
      <p className="text-[11px] text-slate-500">One symbol per line as EXCHANGE:TRADINGSYMBOL (e.g. NSE:NIFTY 50, BSE:SENSEX, NSE:RELIANCE).</p>
      {miss.length > 0 && <p data-testid="config-unresolved" className="text-xs text-amber-400">Unresolved: {miss.join(", ")}</p>}
      <div className="grid gap-3 sm:grid-cols-3">
        <Area label="Always-live indices" value={f.curated_indices} onChange={set("curated_indices")} testid="config-curated-indices" />
        <Area label="Watchlist" value={f.watchlist} onChange={set("watchlist")} testid="config-watchlist" />
        <Area label="Gainers/losers universe" value={f.universe} onChange={set("universe")} testid="config-universe" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Area label="Holidays (YYYY-MM-DD Name)" value={f.holidays} onChange={set("holidays")} testid="config-holidays" rows={5} />
        <Area label="Special sessions (YYYY-MM-DD HH:MM-HH:MM Name)" value={f.special_sessions} onChange={set("special_sessions")} testid="config-special-sessions" rows={5} />
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {NUMS.map(([k, label]) => (
          <div key={k} className="space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <Input data-testid={`config-${k}`} type="number" value={f[k]} onChange={(e) => set(k)(e.target.value)} className="border-slate-800 bg-slate-900 font-mono" />
          </div>
        ))}
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Daily instrument sync (IST)</div>
          <Input data-testid="config-sync-time" value={f.instrument_sync_time_ist} onChange={(e) => set("instrument_sync_time_ist")(e.target.value)} className="border-slate-800 bg-slate-900 font-mono" />
        </div>
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Risk-free rate (Greeks)</div>
          <Input data-testid="config-risk-free-rate" type="number" step="0.001" value={f.risk_free_rate} onChange={(e) => set("risk_free_rate")(e.target.value)} className="border-slate-800 bg-slate-900 font-mono" />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Caps per class (JSON)</div>
          <Input data-testid="config-caps" value={f.caps} onChange={(e) => set("caps")(e.target.value)} className="border-slate-800 bg-slate-900 font-mono text-xs" />
        </div>
      </div>
      <Button data-testid="config-save-button" onClick={save} disabled={busy} className="bg-cyan-500 font-semibold text-slate-950 hover:bg-cyan-400">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save configuration</>}
      </Button>
    </div>
  );
};
